import { AdSlotType } from '../../advertising/AdTypes';
import type { WorldConfig } from '../../config/world.config';
import { DynamicEventType } from '../../dynamic-events/DynamicEventType';
import type { ObstacleType } from '../../obstacles/ObstacleType';
import { DEG_TO_RAD } from '../../utils/math';
import { ConnectorProfile, ConnectorRole, type ConnectorDefinition } from '../connectors/ConnectorDefinition';
import type {
  AdvertisingSlotDefinition,
  CollectibleSpawnPoint,
  DecorationSocket,
  DynamicEventSocket,
  LaneDefinition,
  ObstacleSpawnPoint,
  TrackArea,
} from './SegmentDefinition';

/**
 * Helpers that generate repetitive socket data for segment definitions.
 * Keeps definitions short and free of copy/paste errors.
 */
const LANE_IDS = ['LEFT', 'CENTER', 'RIGHT'] as const;

/** Collectibles float above the road and spin (ructec bottle, user decision 2026-10-01); arcs over obstacles lift them further. */
export const COLLECTIBLE_FLOAT_HEIGHT = 0.5;

/**
 * Backdrop (every sheet): scaffolded shell building beside the yard, scaffold front facing the track.
 * Filled more often since the roadside bays (2026-10-02): the sheets show buildings behind most yards.
 */
export const BACKDROP_SOCKET = { x: 20, footprint: { width: 19, depth: 14 }, fillChance: 0.85 } as const;
/** Tower crane behind the buildings; the jib reaches over the track. */
export const CRANE_SOCKET = { x: 34, footprint: { width: 6, depth: 6 }, fillChance: 0.22 } as const;

/**
 * Backdrop socket beside the yard: shell building (BLD_SHELL_A) or a working machine (tag 'yard');
 * yaw turns the front to the track.
 */
export function backdropSocket(id: string, side: DecorationSocket['side'], x: number, z: number, yaw: number): DecorationSocket {
  return { id, side, position: { x, y: 0, z }, yaw, maxFootprint: BACKDROP_SOCKET.footprint, tags: ['backdrop', 'yard'], fillChance: BACKDROP_SOCKET.fillChance };
}

/** Socket that always carries a shell building (no machines) – e.g. behind a closed junction end. */
export function buildingSocket(id: string, side: DecorationSocket['side'], x: number, z: number, yaw: number): DecorationSocket {
  return { id, side, position: { x, y: 0, z }, yaw, maxFootprint: BACKDROP_SOCKET.footprint, tags: ['backdrop'], fillChance: 1 };
}

/** Backdrop socket for a tower crane (ENV_CRANE_A) – yaw points the jib (local +x). */
export function craneSocket(id: string, side: DecorationSocket['side'], x: number, z: number, yaw: number): DecorationSocket {
  return { id, side, position: { x, y: 0, z }, yaw, maxFootprint: CRANE_SOCKET.footprint, tags: ['backdrop', 'crane'], fillChance: CRANE_SOCKET.fillChance };
}

export function laneCenterX(world: WorldConfig, lane: number): number {
  return (lane - (world.laneCount - 1) / 2) * world.laneWidth;
}

export function standardLanes(world: WorldConfig): LaneDefinition[] {
  return Array.from({ length: world.laneCount }, (_, index) => ({
    index,
    id: LANE_IDS[index] ?? 'CENTER',
    centerX: laneCenterX(world, index),
    width: world.laneWidth,
  }));
}

/** One obstacle spawn point per lane at the given Z. */
export function obstacleRow(
  world: WorldConfig,
  row: string,
  z: number,
  allowedTypes: readonly ObstacleType[],
): ObstacleSpawnPoint[] {
  return Array.from({ length: world.laneCount }, (_, lane) => ({
    id: `SOCKET_OBS_${row}_L${lane}`,
    row,
    lane,
    position: { x: laneCenterX(world, lane), y: 0, z },
    allowedTypes,
  }));
}

/** Collectible spawn points along every lane: from `start` to `end` (inclusive) every `spacing` meters. */
export function collectibleLanes(
  world: WorldConfig,
  start: number,
  end: number,
  spacing: number,
  height: number,
): CollectibleSpawnPoint[] {
  const points: CollectibleSpawnPoint[] = [];
  for (let lane = 0; lane < world.laneCount; lane++) {
    for (let z = start; z <= end + 1e-6; z += spacing) {
      points.push({
        id: `SOCKET_COL_Z${String(Math.round(z)).padStart(2, '0')}_L${lane}`,
        lane,
        position: { x: laneCenterX(world, lane), y: height, z },
      });
    }
  }
  return points;
}

// ── Straight modules of any length (N × 20 m blocks) ───────────────────────

/**
 * Site layout beside the track, per 20 m block – mirrors SEG_STRAIGHT_20M
 * (see SEGMENT_STANDARD.md): roadside bays behind the fence, backdrop building and
 * crane, fence ads, event sockets beside the track.
 */
export const SITE_LAYOUT = {
  blockLength: 20,
  fenceX: 4.2,
  /** The ad plane sits in front of the fence panel. */
  adSurfaceOffset: 0.08,
  adHeight: 1.15,
  adSize: { width: 6, height: 1.5 },
  /** z of the two near-side sockets of a block (bays: 0–10 m and 10–20 m). */
  nearSideZ: [5, 15],
  /**
   * Roadside bays (reference sheets): scaffolded façades, stacked containers, material yards and
   * site offices right behind the fence instead of open gravel. 10 m along the track × 7 m across;
   * the front (x ±4.6) stays 0.4 m behind the fence so its ads stay readable, the back (±11.6)
   * stays clear of the backdrop building's scaffold (±12.97).
   */
  roadside: { x: 8.1, footprint: { width: 10, depth: 7 }, fillChance: 0.9 },
  /** Ground props where no bay fits (inner side of a curve, open terrain, next to a machine). */
  decorationX: 7.5,
  /** Fits a 20 ft container (6.06 × 2.44 m) with some play. */
  decorationFootprint: { width: 6.5, depth: 4 },
  /** Small props right behind the fence (work lights) – only beside ground props, bays bring their own lights. */
  smallDecoration: { x: 5.2, z: 19, footprint: { width: 1.4, depth: 1.4 } },
  eventSideX: 9,
  eventTriggerDistance: 35,
} as const;

/** Socket tag of the roadside bays – only bay assets (ENV_ROADSIDE_*) fit them. */
export const ROADSIDE_TAG = 'roadside';

const FACE_RIGHT = 90 * DEG_TO_RAD;
const FACE_LEFT = -90 * DEG_TO_RAD;

/**
 * Roadside bay socket: the bay's front (local +z) faces the track, its 10 m run along the track
 * (local x). Same frame as the backdrop buildings (BLD_SHELL_A).
 */
export function roadsideBay(id: string, side: 'LEFT' | 'RIGHT', z: number): DecorationSocket {
  const bay = SITE_LAYOUT.roadside;
  const sign = side === 'LEFT' ? -1 : 1;
  return {
    id,
    side,
    position: { x: sign * bay.x, y: 0, z },
    yaw: side === 'LEFT' ? FACE_RIGHT : FACE_LEFT,
    maxFootprint: bay.footprint,
    tags: [ROADSIDE_TAG],
    fillChance: bay.fillChance,
  };
}

/** Which kind of dressing stands right behind the fence. */
export interface NearSideOptions {
  /** Roadside bays (default). false = single ground props and a work light – open terrain, inner side of a curve. */
  readonly roadside?: boolean;
  /** Bay ids that shrink to a single ground prop – a machine works right next to them (hazard modules). */
  readonly groundProps?: readonly string[];
}

/**
 * Near-side dressing of one 20 m block (SOCKET_DECO_<L|R>_01/_02<suffix>, z 5 / 15): two roadside
 * bays, or two ground props plus a work light (_03) where bays do not fit.
 */
export function nearSideSockets(side: 'LEFT' | 'RIGHT', suffix: string, blockStart: number, options: NearSideOptions = {}): DecorationSocket[] {
  const L = SITE_LAYOUT;
  const letter = side === 'LEFT' ? 'L' : 'R';
  const sign = side === 'LEFT' ? -1 : 1;
  const yaw = side === 'LEFT' ? FACE_RIGHT : FACE_LEFT;
  const roadside = options.roadside ?? true;
  const sockets = L.nearSideZ.map((dz, index) => {
    const id = `SOCKET_DECO_${letter}_0${index + 1}${suffix}`;
    const z = blockStart + dz;
    return roadside && !options.groundProps?.includes(id) ? roadsideBay(id, side, z) : ground(id, side, sign * L.decorationX, z, yaw);
  });
  if (!roadside) {
    sockets.push({
      id: `SOCKET_DECO_${letter}_03${suffix}`,
      side,
      position: { x: sign * L.smallDecoration.x, y: 0, z: blockStart + L.smallDecoration.z },
      yaw,
      maxFootprint: L.smallDecoration.footprint,
      tags: ['ground', 'small'],
    });
  }
  return sockets;
}

export function entryConnector(): ConnectorDefinition {
  return {
    id: 'CONNECTOR_IN',
    role: ConnectorRole.In,
    profile: ConnectorProfile.Ground3Lane,
    transform: { x: 0, y: 0, z: 0, yaw: 0 },
    allowedNextProfiles: [],
  };
}

export function straightExit(length: number): ConnectorDefinition {
  return {
    id: 'CONNECTOR_OUT',
    role: ConnectorRole.Out,
    profile: ConnectorProfile.Ground3Lane,
    transform: { x: 0, y: 0, z: length, yaw: 0 },
    allowedNextProfiles: [ConnectorProfile.Ground3Lane],
  };
}

export interface ModuleSides {
  readonly decorationSockets: DecorationSocket[];
  readonly advertisingSlots: AdvertisingSlotDefinition[];
  readonly dynamicEventSockets: DynamicEventSocket[];
}

/**
 * Decoration sockets, fence ads and side event sockets for a straight module,
 * repeated per 20 m block. Ids of the first block match SEG_STRAIGHT_20M, later
 * blocks get a suffix (_2, _3).
 */
export function moduleSides(length: number, options: NearSideOptions = {}): ModuleSides {
  const L = SITE_LAYOUT;
  const sides: ModuleSides = { decorationSockets: [], advertisingSlots: [], dynamicEventSockets: [] };
  for (let block = 0; block * L.blockLength < length - 1e-6; block++) {
    const o = block * L.blockLength;
    const s = block === 0 ? '' : `_${block + 1}`;
    const mid = o + L.blockLength / 2;
    for (const [side, sign, yaw] of [['L', -1, FACE_RIGHT], ['R', 1, FACE_LEFT]] as const) {
      const sideName = side === 'L' ? 'LEFT' : 'RIGHT';
      sides.decorationSockets.push(
        ...nearSideSockets(sideName, s, o, options),
        backdropSocket(`SOCKET_DECO_${side}_04${s}`, sideName, sign * BACKDROP_SOCKET.x, mid, yaw),
        // cranes: the jib (local +x) points across the track
        craneSocket(`SOCKET_DECO_${side}_05${s}`, sideName, sign * CRANE_SOCKET.x, mid, side === 'L' ? 0 : Math.PI),
      );
      sides.advertisingSlots.push({
        id: `AD_FENCE_${sideName}${s}`,
        slotType: side === 'L' ? AdSlotType.FenceLeft : AdSlotType.FenceRight,
        position: { x: sign * (L.fenceX - L.adSurfaceOffset), y: L.adHeight, z: mid },
        yaw,
        width: L.adSize.width,
        height: L.adSize.height,
      });
      sides.dynamicEventSockets.push({
        id: `SOCKET_EVT_SIDE_${side}${s}`,
        position: { x: sign * L.eventSideX, y: 0, z: mid },
        yaw,
        supportedEvents: [DynamicEventType.ForkliftCrossing, DynamicEventType.TruckArrival, DynamicEventType.ExcavatorSwing],
        triggerDistance: L.eventTriggerDistance,
        affectedLanes: [0, 1, 2],
      });
    }
  }
  return sides;
}

/** Ground prop socket (container, pallets, light mast …) – tag 'ground'. */
function ground(id: string, side: DecorationSocket['side'], x: number, z: number, yaw: number): DecorationSocket {
  return { id, side, position: { x, y: 0, z }, yaw, maxFootprint: SITE_LAYOUT.decorationFootprint, tags: ['ground'] };
}

// ── Terrain & spawn helpers ────────────────────────────────────────────────

/** Floor-plan area covering whole lanes `fromLane`…`toLane` (inclusive) between zFrom and zTo. */
export function laneArea(world: WorldConfig, fromLane: number, toLane: number, zFrom: number, zTo: number): TrackArea {
  const half = world.laneWidth / 2;
  return { zFrom, zTo, xFrom: laneCenterX(world, fromLane) - half, xTo: laneCenterX(world, toLane) + half };
}

/** Obstacle spawn points of one row on selected lanes only (standing on ground height y). */
export function obstacleRowOn(
  world: WorldConfig,
  row: string,
  z: number,
  lanes: readonly number[],
  allowedTypes: readonly ObstacleType[],
  y = 0,
): ObstacleSpawnPoint[] {
  return lanes.map((lane) => ({
    id: `SOCKET_OBS_${row}_L${lane}`,
    row,
    lane,
    position: { x: laneCenterX(world, lane), y, z },
    allowedTypes,
  }));
}

/** One stretch of a collectible line: a lane from `from` to `to`, every `spacing` m at height(z). */
export interface CollectibleRun {
  readonly lane: number;
  readonly from: number;
  readonly to: number;
  readonly spacing: number;
  readonly height: (z: number) => number;
}

/** Collectible points of a named line – runs may switch lanes (zig-zag) or follow ramps and arcs. */
export function collectibleLine(world: WorldConfig, line: string, runs: readonly CollectibleRun[]): CollectibleSpawnPoint[] {
  const points: CollectibleSpawnPoint[] = [];
  for (const run of runs) {
    for (let z = run.from; z <= run.to + 1e-6; z += run.spacing) {
      points.push({
        id: `SOCKET_COL_Z${String(Math.round(z)).padStart(2, '0')}_L${run.lane}`,
        lane: run.lane,
        position: { x: laneCenterX(world, run.lane), y: run.height(z), z },
        line,
      });
    }
  }
  return points;
}

/** Height profile of a jump arc: `apex` above `base` at `center`, back to `base` at center ± halfLength. */
export function jumpArc(base: number, apex: number, center: number, halfLength: number): (z: number) => number {
  return (z) => base + apex * Math.max(0, 1 - ((z - center) / halfLength) ** 2);
}
